// frontend-react/src/components/RuleManagement/RuleEditAnalysisTab.tsx
import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Empty, Spin, Alert, Button, Modal, message } from 'antd';
import type { ExpandableConfig } from 'antd/es/table/interface';
import { EditOutlined, DeleteOutlined, SwapOutlined, CaretUpFilled, CaretDownFilled, CloseOutlined, ClearOutlined } from '@ant-design/icons';
import { getRuleEditAnalysis, deleteCardOperation, clearAllCardOperations } from '../../services/ruleService';
import type { RuleEditAnalysisResult, RuleEditStat, RuleEditSample } from '../../types/index';

const { Text } = Typography;

// ── 配色常數（語意一致版）──
// 修改前：中性灰（無立場，只是顯示原始內容）
const BEFORE_BG = '#F5F7FA';
const BEFORE_BORDER = '#9CA3AF';
const BEFORE_LABEL = '#6B7280';
// 修改後 / 編輯：品牌青藍（「編輯」動作 = 藍，與編輯次數 tag 一致）
const AFTER_EDIT_BG = '#E6F4F8';
const AFTER_EDIT_BORDER = '#1E88A8';
const AFTER_EDIT_LABEL = '#1E88A8';
// 刪除後空白：淡橘色（「刪除」動作 = 橘，與刪除次數 tag 一致）
const AFTER_DELETE_BG = '#FFF7ED';
const AFTER_DELETE_BORDER = '#FB923C';
const AFTER_DELETE_LABEL = '#EA580C';
// 引用變更 — 橄欖綠（與 AI 優化建議一致）
const RULE_CHANGE_BG = '#F0F7F2';
const RULE_CHANGE_BORDER = '#6B7B5A';
const RULE_CHANGE_LABEL = '#4A5E3A';

interface Props {
  ruleSetId: number;
}

const DiffBox: React.FC<{
  label: string;
  content: string;
  bg: string;
  border: string;
  labelColor: string;
  empty?: boolean;
}> = ({ label, content, bg, border, labelColor, empty = false }) => (
  <div style={{ minWidth: 0, overflow: 'hidden' }}>
    <div style={{ fontSize: 11, color: labelColor, fontWeight: 600, marginBottom: 4 }}>
      {label}
    </div>
    <div
      style={{
        background: bg,
        borderLeft: `3px solid ${border}`,
        borderRadius: '0 4px 4px 0',
        padding: '6px 10px',
        fontSize: 12,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        minHeight: 36,
        color: empty ? 'transparent' : '#374151',
        userSelect: empty ? 'none' : undefined,
      }}
    >
      {empty ? '　' : content || '（無）'}
    </div>
  </div>
);

const SampleDiff: React.FC<{ sample: RuleEditSample; onRemove?: (id: number) => void }> = ({ sample, onRemove }) => {
  const before = sample.before_data ?? {};
  const after = sample.after_data ?? {};
  const isDelete = sample.operation === 'delete';
  const isRuleChange = !!sample.is_rule_change;

  return (
    <div
      style={{
        padding: '10px 12px',
        borderBottom: '1px solid #F3F4F6',
        background: '#FFFFFF',
        borderRadius: 6,
        marginBottom: 6,
        position: 'relative',
      }}
    >
      {/* 右上角刪除按鈕 */}
      {onRemove && (
        <span
          role='button'
          title='移除此紀錄'
          onClick={e => { e.stopPropagation(); onRemove(sample.id); }}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            cursor: 'pointer',
            color: '#9CA3AF',
            fontSize: 14,
            lineHeight: 1,
            padding: 4,
            borderRadius: 4,
            transition: 'color 0.2s, background 0.2s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#EF4444'; (e.currentTarget as HTMLElement).style.background = '#FEF2F2'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#9CA3AF'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          <CloseOutlined />
        </span>
      )}
      {/* 操作標籤 + 時間 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Tag
          icon={isRuleChange ? <SwapOutlined /> : isDelete ? <DeleteOutlined /> : <EditOutlined />}
          style={{
            background: isRuleChange ? RULE_CHANGE_BG : isDelete ? AFTER_DELETE_BG : AFTER_EDIT_BG,
            border: `1px solid ${isRuleChange ? RULE_CHANGE_BORDER : isDelete ? AFTER_DELETE_BORDER : AFTER_EDIT_BORDER}`,
            color: isRuleChange ? RULE_CHANGE_LABEL : isDelete ? AFTER_DELETE_LABEL : AFTER_EDIT_LABEL,
            fontWeight: 600,
            margin: 0,
          }}
        >
          {isRuleChange ? '引用變更' : isDelete ? '刪除' : '編輯'}
        </Tag>
        {sample.created_at && (
          <Text type='secondary' style={{ fontSize: 11 }}>
            {new Date(sample.created_at).toLocaleString('zh-TW', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'Asia/Taipei',
            })}
          </Text>
        )}
      </div>

      {/* 引用變更：顯示規則名稱的 before → after */}
      {isRuleChange && (
        <div style={{ marginBottom: 8, fontSize: 12 }}>
          <span style={{ color: '#6B7280', fontWeight: 500 }}>引用規則：</span>
          <span style={{ color: '#374151' }}>{sample.rule_change_from || before.rule || '（未知）'}</span>
          <span style={{ color: RULE_CHANGE_LABEL, margin: '0 6px', fontWeight: 600 }}>→</span>
          <span style={{ color: RULE_CHANGE_LABEL, fontWeight: 600 }}>{sample.rule_change_to || after.rule || '（未知）'}</span>
        </div>
      )}

      {/* 文件原文 */}
      {before.origin && (
        <div style={{ marginBottom: 8, fontSize: 12, color: '#6B7280' }}>
          <span style={{ fontWeight: 500 }}>文件原文：</span>
          <span style={{ color: '#374151' }}>{before.origin}</span>
        </div>
      )}

      {/* Before / After 並排（rule_change 時顯示建議文字的前後） */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <DiffBox
          label='修改前 AI 建議'
          content={before.suggestion ?? ''}
          bg={BEFORE_BG}
          border={BEFORE_BORDER}
          labelColor={BEFORE_LABEL}
        />
        <DiffBox
          label={isDelete ? '刪除後（空白）' : '修改後內容'}
          content={after.suggestion ?? ''}
          bg={isRuleChange ? RULE_CHANGE_BG : isDelete ? AFTER_DELETE_BG : AFTER_EDIT_BG}
          border={isRuleChange ? RULE_CHANGE_BORDER : isDelete ? AFTER_DELETE_BORDER : AFTER_EDIT_BORDER}
          labelColor={isRuleChange ? RULE_CHANGE_LABEL : isDelete ? AFTER_DELETE_LABEL : AFTER_EDIT_LABEL}
          empty={isDelete}
        />
      </div>
    </div>
  );
};

type SortField = 'rule_index' | 'edit_count' | 'delete_count' | 'rule_change_out_count';
type SortOrder = 'ascend' | 'descend';

const SortIcon: React.FC<{
  field: SortField;
  sortField: SortField;
  sortOrder: SortOrder;
}> = ({ field, sortField, sortOrder }) => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: 4, verticalAlign: 'middle', gap: 0 }}>
    <CaretUpFilled style={{ fontSize: 11, display: 'block', lineHeight: 0, color: sortField === field && sortOrder === 'ascend' ? '#1E88A8' : '#D1D5DB' }} />
    <CaretDownFilled style={{ fontSize: 11, display: 'block', lineHeight: 0, marginTop: -4, color: sortField === field && sortOrder === 'descend' ? '#1E88A8' : '#D1D5DB' }} />
  </span>
);

const RuleEditAnalysisTab: React.FC<Props> = ({ ruleSetId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RuleEditAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('edit_count');
  const [sortOrder, setSortOrder] = useState<SortOrder>('descend');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'descend' ? 'ascend' : 'descend');
    } else {
      setSortField(field);
      setSortOrder(field === 'rule_index' ? 'ascend' : 'descend');
    }
  };

  const handleRemoveSample = async (operationId: number) => {
    const ok = await deleteCardOperation(ruleSetId, operationId);
    if (!ok || !data) return;
    // 從本地 data 移除該筆紀錄，避免重新拉 API
    const updated: RuleEditAnalysisResult = {
      ...data,
      rules: data.rules.map(rule => {
        const filtered = rule.samples.filter(s => s.id !== operationId);
        if (filtered.length === rule.samples.length) return rule;
        // 重新計算該規則的 count
        let editCount = 0;
        let deleteCount = 0;
        let ruleChangeOutCount = 0;
        let ruleChangeInCount = 0;
        for (const s of filtered) {
          if (s.is_rule_change) {
            ruleChangeOutCount++;
            ruleChangeInCount++;
          } else if (s.operation === 'edit') {
            editCount++;
          } else if (s.operation === 'delete') {
            deleteCount++;
          }
        }
        return { ...rule, samples: filtered, edit_count: editCount, delete_count: deleteCount, rule_change_out_count: ruleChangeOutCount, rule_change_in_count: ruleChangeInCount };
      }),
    };
    // 重算 totals
    updated.total_edits = updated.rules.reduce((s, r) => s + r.edit_count, 0);
    updated.total_deletes = updated.rules.reduce((s, r) => s + r.delete_count, 0);
    updated.total_rule_changes = updated.rules.reduce((s, r) => s + (r.rule_change_out_count ?? 0), 0);
    setData(updated);
  };

  const [clearing, setClearing] = useState(false);

  const handleClearAll = () => {
    if (!data) return;
    const total = data.total_edits + data.total_deletes + (data.total_rule_changes ?? 0);
    Modal.confirm({
      title: '確定要清空所有編輯紀錄嗎？',
      content: `將刪除此規則集下共 ${total} 筆編輯紀錄，此操作無法復原。`,
      okText: '確定清空',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setClearing(true);
        try {
          const result = await clearAllCardOperations(ruleSetId);
          message.success(`已清空 ${result.deleted_count} 筆編輯紀錄`);
          // 清空本地 data
          setData({
            ...data,
            total_edits: 0,
            total_deletes: 0,
            total_rule_changes: 0,
            unmatched_edits: 0,
            rules: data.rules.map(r => ({
              ...r,
              edit_count: 0,
              delete_count: 0,
              rule_change_out_count: 0,
              rule_change_in_count: 0,
              samples: [],
            })),
          });
          setExpandedKeys([]);
        } catch {
          message.error('清空編輯紀錄失敗，請稍後再試');
        } finally {
          setClearing(false);
        }
      },
    });
  };

  const baseExpandable: Omit<ExpandableConfig<RuleEditStat>, 'expandedRowKeys' | 'onExpandedRowsChange'> = {
    rowExpandable: (record: RuleEditStat) => record.samples.length > 0,
    expandedRowRender: (record: RuleEditStat) => (
      <div style={{ padding: '8px 16px', background: '#FAFAFA', borderRadius: 6 }}>
        {record.samples.map((s: RuleEditSample, i: number) => (
          <SampleDiff key={s.id ?? i} sample={s} onRemove={handleRemoveSample} />
        ))}
        {record.samples.length === 10 && (
          <Text type='secondary' style={{ fontSize: 12 }}>
            ＊僅顯示最近 10 筆紀錄
          </Text>
        )}
      </div>
    ),
  };

  const headerStyle: React.CSSProperties = {
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getRuleEditAnalysis(ruleSetId)
      .then(result => {
        if (cancelled) return;
        setData(result);
        if (!result) setError('無法取得編輯分析數據');
      })
      .catch(() => {
        if (!cancelled) setError('取得編輯分析失敗，請稍後再試');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ruleSetId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin tip='載入編輯紀錄中...' />
      </div>
    );
  }

  if (error || !data) {
    return <Alert type='error' message={error ?? '未知錯誤'} />;
  }

  const hasAnyEdits = data.total_edits + data.total_deletes + (data.total_rule_changes ?? 0) > 0;

  // 只保留有編輯、刪除或引用變更紀錄的規則，再依目前排序狀態排序
  const dir = sortOrder === 'ascend' ? 1 : -1;
  const sortedRules = data.rules
    .filter(r => r.edit_count + r.delete_count + (r.rule_change_out_count ?? 0) + (r.rule_change_in_count ?? 0) > 0)
    .sort((a, b) => dir * ((a[sortField] ?? 0) - (b[sortField] ?? 0)));

  // 規則名稱 → 條號對照（用於顯示路徑如「1 → 15」）
  const ruleNameToIndex: Record<string, number> = {};
  for (const r of data.rules) {
    if (r.rule_name && r.rule_index > 0) ruleNameToIndex[r.rule_name] = r.rule_index;
  }
  const resolveIdx = (name: string): number | null => {
    const exact = ruleNameToIndex[name];
    if (exact != null) return exact;
    for (const [rn, idx] of Object.entries(ruleNameToIndex)) {
      if (rn.includes(name) || name.includes(rn)) return idx;
    }
    return null;
  };

  const columns = [
    {
      title: (
        <span style={headerStyle} onClick={() => handleSort('rule_index')}>
          規則名稱
          <SortIcon field='rule_index' sortField={sortField} sortOrder={sortOrder} />
        </span>
      ),
      dataIndex: 'rule_name',
      key: 'rule_name',
      render: (name: string, record: RuleEditStat) => (
        <Text style={{ fontWeight: name === '（無法對應規則）' ? 'normal' : 500 }}>
          {record.rule_index > 0 && (
            <span style={{ color: '#9CA3AF', marginRight: 4 }}>第 {record.rule_index} 條｜</span>
          )}
          {name}
        </Text>
      ),
    },
    {
      title: (
        <span style={headerStyle} onClick={() => handleSort('edit_count')}>
          編輯次數
          <SortIcon field='edit_count' sortField={sortField} sortOrder={sortOrder} />
        </span>
      ),
      dataIndex: 'edit_count',
      key: 'edit_count',
      width: 100,
      align: 'center' as const,
      render: (count: number) =>
        count > 0 ? (
          <Tag style={{ background: count >= 3 ? '#CCE9F2' : '#E6F4F8', border: '1px solid #1E88A8', color: count >= 3 ? '#0A4A5E' : '#1E88A8', fontWeight: 600 }}>
            {count} 次
          </Tag>
        ) : (
          <Text type='secondary'>—</Text>
        ),
    },
    {
      title: (
        <span style={headerStyle} onClick={() => handleSort('delete_count')}>
          刪除次數
          <SortIcon field='delete_count' sortField={sortField} sortOrder={sortOrder} />
        </span>
      ),
      dataIndex: 'delete_count',
      key: 'delete_count',
      width: 100,
      align: 'center' as const,
      render: (count: number) =>
        count > 0 ? (
          <Tag style={{ background: '#FFF7ED', border: '1px solid #FB923C', color: '#EA580C', fontWeight: 600 }}>
            {count} 次
          </Tag>
        ) : (
          <Text type='secondary'>—</Text>
        ),
    },
    {
      title: (
        <span style={headerStyle} onClick={() => handleSort('rule_change_out_count')}>
          引用變更
          <SortIcon field='rule_change_out_count' sortField={sortField} sortOrder={sortOrder} />
        </span>
      ),
      key: 'rule_change',
      width: 140,
      align: 'center' as const,
      render: (_: unknown, record: RuleEditStat) => {
        const out = record.rule_change_out_count ?? 0;
        const inCount = record.rule_change_in_count ?? 0;
        if (out === 0 && inCount === 0) return <Text type='secondary'>—</Text>;

        // 從 samples 萃取混淆的「對方規則」條號並計次
        const peerCounts: Record<number, number> = {};
        for (const s of record.samples) {
          if (!s.is_rule_change) continue;
          const fromName = s.rule_change_from ?? '';
          const toName   = s.rule_change_to ?? '';
          // 「對方」= 不是自己的那一方
          const fromIdx = resolveIdx(fromName);
          const toIdx   = resolveIdx(toName);
          const peerIdx = fromIdx === record.rule_index ? toIdx : fromIdx;
          if (peerIdx != null) {
            peerCounts[peerIdx] = (peerCounts[peerIdx] ?? 0) + 1;
          }
        }

        const peers = Object.entries(peerCounts)
          .map(([idx, count]) => ({ idx: Number(idx), count }))
          .sort((a, b) => b.count - a.count);

        if (peers.length === 0) {
          return (
            <Tag style={{ background: RULE_CHANGE_BG, border: `1px solid ${RULE_CHANGE_BORDER}`, color: RULE_CHANGE_LABEL, fontWeight: 600, margin: 0, fontSize: 11 }}>
              {out + inCount} 次
            </Tag>
          );
        }

        // 格式：「15 ×2, 7」
        const label = peers
          .map(p => p.count > 1 ? `${p.idx} ×${p.count}` : `${p.idx}`)
          .join(', ');

        return (
          <Tag style={{ background: RULE_CHANGE_BG, border: `1px solid ${RULE_CHANGE_BORDER}`, color: RULE_CHANGE_LABEL, fontWeight: 600, margin: 0, fontSize: 11 }}>
            {label}
          </Tag>
        );
      },
    },
  ];

  // 有被修改過的規則數量（含規則引用變更）
  const modifiedRulesCount = data.rules.filter(
    r => r.edit_count + r.delete_count + (r.rule_change_out_count ?? 0) + (r.rule_change_in_count ?? 0) > 0
  ).length;

  return (
    <div>
      {/* 統計摘要 */}
      <div
        style={{
          display: 'flex',
          gap: 24,
          marginBottom: 16,
          background: '#FAFAFA',
          borderRadius: 8,
          padding: '12px 16px',
          border: '1px solid #F0F0F0',
        }}
      >
        <div>
          <Text type='secondary' style={{ fontSize: 12 }}>有修改的規則數</Text>
          <div>
            <Text strong style={{ fontSize: 20, color: '#374151' }}>
              {modifiedRulesCount}
            </Text>
          </div>
        </div>
        <div style={{ width: 1, background: '#E5E7EB', margin: '0 4px' }} />
        <div>
          <Text type='secondary' style={{ fontSize: 12 }}>總編輯次數</Text>
          <div>
            <Text strong style={{ fontSize: 20, color: '#B45309' }}>
              {data.total_edits}
            </Text>
          </div>
        </div>
        <div>
          <Text type='secondary' style={{ fontSize: 12 }}>總刪除次數</Text>
          <div>
            <Text strong style={{ fontSize: 20, color: '#EA580C' }}>
              {data.total_deletes}
            </Text>
          </div>
        </div>
        {(data.total_rule_changes ?? 0) > 0 && (
          <div>
            <Text type='secondary' style={{ fontSize: 12 }}>引用變更</Text>
            <div>
              <Text strong style={{ fontSize: 20, color: RULE_CHANGE_LABEL }}>
                {data.total_rule_changes}
              </Text>
            </div>
          </div>
        )}
        {data.unmatched_edits > 0 && (
          <div>
            <Text type='secondary' style={{ fontSize: 12 }}>無法對應規則</Text>
            <div>
              <Text type='secondary' style={{ fontSize: 20 }}>
                {data.unmatched_edits}
              </Text>
            </div>
          </div>
        )}
        {hasAnyEdits && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <Button
              size='small'
              icon={<ClearOutlined />}
              loading={clearing}
              onClick={handleClearAll}
              style={{ color: '#6B7280', borderColor: '#D1D5DB' }}
            >
              清空紀錄
            </Button>
          </div>
        )}
      </div>

      {!hasAnyEdits ? (
        <Empty
          description='此規則集目前尚無任何卡片編輯紀錄'
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Table<RuleEditStat>
          columns={columns}
          dataSource={sortedRules}
          rowKey='rule_name'
          expandable={{
            ...baseExpandable,
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: keys => setExpandedKeys([...keys].map(String)),
          }}
          onRow={record => ({
            style: record.samples.length > 0 ? { cursor: 'pointer' } : undefined,
            onClick: () => {
              if (record.samples.length === 0) return;
              const key = record.rule_name;
              setExpandedKeys(prev =>
                prev.indexOf(key) >= 0 ? prev.filter(k => k !== key) : [...prev, key]
              );
            },
          })}
          pagination={false}
          size='small'
          scroll={{ x: '100%' }}
          rowClassName={record =>
            record.edit_count + record.delete_count === 0 ? 'ant-table-row-disabled' : ''
          }
        />
      )}
    </div>
  );
};

export default RuleEditAnalysisTab;
